import { useEffect, useRef } from 'react';
import { SpaceBetween } from '@cloudscape-design/components';
import { Audit, QuestionWithResponse } from 'src/types/helios/Audit';
import { Task } from 'src/types/helios/Task';
import { Task as ClientTask, TaskStatus } from '@amzn/gbsa-helios-typescript-client';
import { TaskAuditQuestionResponsePanel } from 'src/components/task/TaskAuditQuestionResponsePanel';
import { SplitPanelErrorBoundary } from 'src/components/SplitPanelErrorBoundary';
import TaskDraftPanel from 'src/components/audit/perform-audit/TaskDraftPanel';
import { useSplitPanel } from 'src/context/SplitPanelContext';
import useUserProfile from 'src/hooks/useUserProfile';

interface AuditSummaryTaskPanelProps {
  /** Whether the panel is visible */
  visible: boolean;
  /** Callback to close the panel */
  onDismiss: () => void;
  /** The audit being viewed */
  audit: Audit;
  /** The question for which a task is being created */
  question: QuestionWithResponse;
  /** Optional sub-question ID if task is for a sub-question */
  subQuestionId?: string;
  /** Existing task if one was already created for this question */
  existingTask?: Task;
  /** When true the task is in a terminal state (cancelled/completed) and cannot be edited */
  readOnly?: boolean;
  /** Callback after successful task creation */
  onTaskCreated?: () => void;
}

/**
 * Side panel for creating a task draft from a failed audit question.
 * Uses the app-level SplitPanel (via SplitPanelContext) to display:
 * 1. TaskAuditQuestionResponsePanel — question details, pass/fail criteria, evidence (reused from task-details page)
 * 2. TaskDraftPanel — task creation form with mutation logic (reused from perform-audit page)
 */
const AuditSummaryTaskPanel = ({
  visible,
  onDismiss,
  audit,
  question,
  subQuestionId,
  existingTask,
  readOnly = false,
  onTaskCreated,
}: AuditSummaryTaskPanelProps) => {
  const { setContent, setTitle, closeView, setPanelSize } = useSplitPanel();
  const { getUserBusinessUnitId, getUserProgramId } = useUserProfile();
  const businessUnitId = audit.businessUnitId || audit.businessUnit;

  // Use refs for callbacks to avoid stale closures in setContent JSX
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  const onTaskCreatedRef = useRef(onTaskCreated);
  onTaskCreatedRef.current = onTaskCreated;

  const content = question?.questionDetails?.localizedContent?.content;
  const displayTitle = subQuestionId
    ? content?.subQuestions?.find((sq) => sq.subQuestionId === subQuestionId)?.title
    : content?.questionDetails?.title;
  const defaultTask = subQuestionId
    ? content?.subQuestions?.find((sq) => sq.subQuestionId === subQuestionId)?.taskDetails
    : content?.taskDetails;

  useEffect(() => {
    if (!visible || !question) {
      if (!visible) closeView();
      return;
    }

    setPanelSize(window.innerWidth * 0.3);
    setTitle(readOnly ? 'View Task' : existingTask ? 'Edit Task' : 'Create Task');
    setContent(
      <SpaceBetween size="l">
        <TaskDraftPanel
          key={`${question.questionDetails.questionId}-${subQuestionId ?? ''}`}
          audit={audit}
          questionId={question.questionDetails.questionId}
          questionVersion={question.questionDetails.version?.toString() ?? ''}
          subQuestionId={subQuestionId}
          questionTitle={displayTitle}
          defaultTask={defaultTask}
          existingDraft={existingTask as unknown as ClientTask}
          businessUnitId={businessUnitId}
          divisionName={audit.divisionName}
          headerFontSize="heading-s"
          submitButtonText={existingTask ? 'Update Task' : 'Create Task'}
          hideOptionalLabel
          hideHeaderOptionalLabel
          headerColor="text-label"
          hideExpandableWrapper
          taskStatusOverride={TaskStatus.ASSIGNED}
          disabled={readOnly}
          onMutationSuccess={() => {
            closeView();
            onDismissRef.current();
            onTaskCreatedRef.current?.();
          }}
        />

        <SplitPanelErrorBoundary onReset={closeView}>
          <TaskAuditQuestionResponsePanel
            questionId={question.questionDetails.questionId}
            questionVersion={question.questionDetails.version?.toString()}
            subQuestionId={subQuestionId}
            auditId={audit.auditId}
            businessUnit={getUserBusinessUnitId()}
            divisionName={audit.divisionName}
            programId={getUserProgramId()}
            headerText="Audit Details"
          />
        </SplitPanelErrorBoundary>
      </SpaceBetween>,
    );
  }, [visible, question, subQuestionId, existingTask, readOnly]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

export default AuditSummaryTaskPanel;
